export const formatHours = (decimalHours) => {
  const isNegative = decimalHours < 0;
  const absHours = Math.abs(decimalHours);
  const hours = Math.floor(absHours);
  const minutes = Math.round((absHours - hours) * 60);
  const sign = isNegative ? '-' : '';
  return `${sign}${hours}:${minutes.toString().padStart(2, '0')}h`;
};

// The calendar date of a Date as YYYY-MM-DD, in the browser's own time zone. This
// is what a log's CRM Date must hold, because transformTimelogsToEvents reads it
// back as a local date. Never use toISOString().split('T')[0] for this: that is the
// UTC date, so a log starting before 02:00 Copenhagen summer time (01:00 in winter)
// was saved on the previous day and jumped back a day in the calendar.
export const localDateKey = (date) => {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// The calendar is a Copenhagen clock for everyone: it draws a log from the Danish
// clock text CRM holds, as if that were local time, so what a user drags or types
// IS Danish time. A time is therefore sent as that clock text without an offset
// ("2026-10-09T20:00:00") and the server reads it as Europe/Copenhagen. Sending
// the browser's instant instead moved every log by the browser's distance from
// Danish time, and moved it again on each re-save.
export const wallClock = (value) => {
  const d = new Date(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${localDateKey(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

// A timelog write payload with its start and end as Danish clock text.
export const withWallClockTimes = (payload) => ({
  ...payload,
  ...(payload.start != null ? { start: wallClock(payload.start) } : {}),
  ...(payload.end != null ? { end: wallClock(payload.end) } : {}),
});

// Shortest event we will draw, so a zero-duration log stays visible and
// clickable instead of collapsing to nothing.
const MIN_RENDER_MS = 60 * 1000;

// A log CapLegal has deleted. Two shapes, because the marker changed:
//   minutes === '0'  the current marker, written by the CL sync the moment CapLegal
//                    zeroes an entry. The CRM record survives 14 days so a wrong
//                    deletion can be spotted, then a schedule deletes it.
//   start === end    the older shape, from entries created empty in CapLegal and
//                    imported before the sync learned to skip them. These used to
//                    render as a 23 hour block, then as a one minute sliver.
// Neither is real work, so neither may be drawn or counted. A log with NO times at
// all is left alone deliberately: that is broken data rather than a deletion, and
// hiding it would make it unfixable.
function isDeletedInCapLegal(log) {
  if (String(log.minutes ?? '').trim() === '0') return true;
  return !!log.start && !!log.end && log.start === log.end;
}

// Transform the flat /api/timelogs response (Zoho CRM Timelogs) into calendar events
export function transformTimelogsToEvents(flatLogs) {
  if (!Array.isArray(flatLogs)) return [];

  return flatLogs.filter((log) => !isDeletedInCapLegal(log)).map((log) => {
    const [yyyy, mm, dd] = log.date.split('-').map(Number);
    const dateBase = new Date(yyyy, mm - 1, dd);

    const [startHour, startMin] = (log.start || '00:00').split(':').map(Number);
    const [endHour, endMin] = (log.end || '00:00').split(':').map(Number);

    let start = new Date(dateBase);
    start.setHours(startHour, startMin, 0, 0);
    let end = new Date(dateBase);
    end.setHours(endHour, endMin, 0, 0);

    // Only a genuine midnight crossing moves to the next day. An end equal to its
    // start is a zero-length log, not 24 hours: it used to become 24, then get
    // clamped to 23, which is how CapLegal entry 1241937 drew as a 23-hour block
    // and inflated one Monday to 26:50. Same rule as utils/durationRule.js, which
    // covers the two time-entry modals; this covers everything loaded from CRM.
    if (end < start) {
      end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
    }
    // A zero-length event draws as a hairline the user cannot click, so the broken
    // log would look like it had vanished rather than being fixable. One minute
    // keeps it selectable, and matches what the CL sync will store once its own
    // zero-duration fix lands, at which point this floor becomes a no-op.
    if (end - start < MIN_RENDER_MS) {
      end = new Date(start.getTime() + MIN_RENDER_MS);
    }
    const durationHours = (end - start) / (1000 * 60 * 60);
    if (durationHours > 23) {
      end = new Date(start.getTime() + 23 * 60 * 60 * 1000);
    }

    return {
      title: log.description || '',
      start,
      end,
      projectId: log.matterId || '',
      timecodeId: log.workTypeId || '',
      projectName: log.matterName || '',
      timecodeName: log.workTypeName || '',
      approval_status: log.approvalStatus || '',
      billingtype: log.billingType || '',
      logId: log.id,
      outlookSync: !!log.outlookSync,
      sendToCapLegal: !!log.sendToCapLegal,
      createdBy: '',
    };
  });
}
